import { animate, style, transition, trigger } from '@angular/animations';
import { NgClass, NgForOf, NgIf } from '@angular/common';
import { Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatOption } from '@angular/material/core';
import { MatDatepicker, MatDatepickerInput, MatDatepickerToggle } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatInput, MatSuffix } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { MatTimepicker, MatTimepickerInput, MatTimepickerToggle } from '@angular/material/timepicker';
import { MatTooltip } from '@angular/material/tooltip';
import moment from 'moment';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AdminService } from '../../../admin/admin.service';
import { DataToolsApps } from '../../../shared/DataToolsApps';
import { DataToolsLiteMessage } from '../../../shared/DataToolsLiteMessage';
import { DatatoolsLiteService } from '../../../shared/services/datatools-lite.service';
import { AiContentDisclaimerComponent } from '../../ai-content-disclaimer/ai-content-disclaimer.component';
import { AiContentDisclaimerVariant, ruleAiContentDisclaimerVariant } from '../../ai-content.util';
import { DataRulesConfirmDialogComponent } from '../../data-rules-confirm-dialog/data-rules-confirm-dialog.component';
import { DataRuleId, IDataRule, ISchedulePeriod, IScheduleUnit } from '../../data-rules-model';
import { ScheduleData, ScheduleDataService } from './schedule-data.service';
import { ScheduleService } from './schedule.service';

@Component({
  selector: 'app-rule-schedule',
  templateUrl: './rule-schedule.component.html',
  styleUrls: ['./rule-schedule.component.scss'],
  imports: [
    AiContentDisclaimerComponent,
    NgIf,
    ReactiveFormsModule,
    MatTooltip,
    MatSelect,
    MatOption,
    NgClass,
    NgForOf,
    MatInput,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatDatepicker,
    MatSuffix,
    MatTimepickerInput,
    MatTimepickerToggle,
    MatTimepicker
  ],
  animations: [
    trigger('transitionNewRowInOut', [
      transition(':enter', [
        style({ maxHeight: '0', overflow: 'hidden', padding: '0' }),
        animate('.2s ease', style({ maxHeight: '73.5px', padding: '17px 0' })),
      ]),
      transition(':leave', [
        style({ maxHeight: '73.5px', padding: '17px 0' }),
        animate('.2s ease', style({ maxHeight: '0', overflow: 'hidden', padding: '0' })),
      ]),
    ]),
  ]
})
export class RuleScheduleComponent implements OnInit, OnDestroy {

  @Input() dataRule: IDataRule;
  @Input() isRuleFirstInAChain: (ruleId: DataRuleId) => boolean;
  @Output() scheduleSaving = new EventEmitter<any>();
  @Output() scheduleDeleting = new EventEmitter<any>();
  @ViewChild('schedulePopupContainer') schedulePopupContainer;

  scheduleData: ScheduleData;

  scheduleForm: FormGroup;
  scheduleActivated: boolean;
  formReady: boolean;
  foreverExecution: boolean;
  immediateExecution: boolean;

  scheduleUnit: string;
  schedulePeriod: number;
  startDate: string;
  startTime: string | moment.Moment;
  endDate: string;
  minStopDate: string | moment.Moment | Date;

  scheduleUnits: IScheduleUnit[];
  schedulePeriods: ISchedulePeriod[];
  currentPeriods: ISchedulePeriod[];

  scheduleRule: Pick<ScheduleData, 'startTime' | 'stopTime' | 'period' | 'scheduleUnit' | 'enabled'> =
    {
      startTime: '',
      stopTime: '',
      period: 0,
      scheduleUnit: '',
      enabled: false,
    };
  stopDateIsBeforeStartDate: boolean;
  currentDate = new Date();

  deleteDialogOpen: boolean;
  isExpired: boolean;
  aiContentDisclaimerVariant: AiContentDisclaimerVariant | null = null;

  constructor(
    private formBuilder: FormBuilder,
    private scheduleDataService: ScheduleDataService,
    private dialog: MatDialog,
    private adminService: AdminService,
    private datatoolsLiteService: DatatoolsLiteService
  ) {
  }

  @HostListener('document:keydown', ['$event'])
  protected handleKeydownEvent(event: KeyboardEvent) {
    if (event.code === 'Escape' && !this.deleteDialogOpen) {
      this.dataRule.viewState.scheduleOpen = false;
    }
  }

  ngOnInit() {
    this.onClick = this.onClick.bind(this);
    setTimeout(_ => { document.addEventListener('click', this.onClick); }, 0);

    this.aiContentDisclaimerVariant = this.adminService.checkAppEnabled(DataToolsApps.DATARULES_AI_ASSIST)
      ? ruleAiContentDisclaimerVariant(this.dataRule)
      : null;

    this.scheduleUnits = ScheduleService.getUnits();
    this.schedulePeriods = ScheduleService.getPeriods();
    this.refreshScheduleData().subscribe(() => {
      this.initialValues();
      this.buildForm();
      if (this.immediateExecution) this.onImmediatelySelected();
      if (this.foreverExecution) this.onForeverSelected();
    });
  }

  onClick(event: MouseEvent) {
    const clickedInside = this.schedulePopupContainer.nativeElement.contains(event.target) || this.deleteDialogOpen || (event.target as HTMLElement).closest('.cdk-overlay-container');
    if (!clickedInside) {
      this.dataRule.viewState.scheduleOpen = false;
    }
  }

  disableCloseListener(): void {
    document.removeEventListener('click', this.onClick);
  }

  enableCloseListener(): void {
    setTimeout(_ => { document.addEventListener('click', this.onClick); }, 0);
  }

  private buildForm() {
    this.scheduleForm = this.formBuilder.group({
      immediatelyCheckbox: new FormControl(this.immediateExecution),
      startDate: new FormControl(this.startDate ? moment(this.startDate) : this.startDate, Validators.required),
      startTime: new FormControl(this.startTime, Validators.required),
      stopDate: new FormControl(this.endDate ? moment(this.endDate) : this.endDate, Validators.required),
      period: new FormControl(this.schedulePeriod, Validators.required),
      scheduleUnit: new FormControl(this.scheduleUnit, Validators.required),
      foreverIndicator: new FormControl(this.foreverExecution)
    });
    this.formReady = true;
    this.minStopDate = this.scheduleForm.controls['startDate'].value || this.currentDate;
    this.scheduleForm.valueChanges
      .subscribe(value => {
        this.scheduleRule.startTime = this.scheduleForm.controls['startDate'].value;
        this.scheduleRule.stopTime = this.scheduleForm.controls['stopDate'].value;
        this.scheduleRule.period = this.scheduleForm.controls['period'].value;
        this.scheduleRule.scheduleUnit = this.scheduleForm.controls['scheduleUnit'].value;
        this.immediateExecution = this.scheduleForm.controls['immediatelyCheckbox'].value;
        this.foreverExecution = this.scheduleForm.controls['foreverIndicator'].value;
        console.log(value);
      });
    this.scheduleForm.controls['startDate'].valueChanges.subscribe(_ => {
      this.compareStartEndDates();
    });
    this.scheduleForm.controls['stopDate'].valueChanges.subscribe(_ => this.compareStartEndDates());
    this.scheduleForm.controls['scheduleUnit'].valueChanges.subscribe(value => this.onUnitSelected(value));
    this.isScheduleExpired();
  }

  isScheduleExpired(): void {
    const dateNow = new Date();
    const controls = this.scheduleForm.controls;
    this.isExpired = controls['stopDate'].value !== null && (new Date(controls['stopDate'].value) < dateNow);
  }

  initialValues(): void {
    if (this.scheduleData) {

      this.scheduleUnit = this.scheduleData.scheduleUnit;
      this.schedulePeriod = this.scheduleData.period;
      this.currentPeriods = this.schedulePeriods.filter(period => period.idUnit === this.scheduleUnit);
      this.scheduleActivated = this.scheduleData?.enabled;

      if (this.scheduleData?.startTime) {
        const scheduleStartDate = this.scheduleData.startTime;
        this.startDate = scheduleStartDate.substring(0, scheduleStartDate.length - 9);
        this.startTime = moment(scheduleStartDate, 'YYYY-MM-DD HH:mm:ss');
      } else {
        this.immediateExecution = true;
      }

      if (this.scheduleData?.stopTime) {
        const scheduleEndDate = this.scheduleData.stopTime;
        this.endDate = scheduleEndDate.substring(0, scheduleEndDate.length - 9);
      } else {
        this.foreverExecution = true;
      }

    } else {
      this.schedulePeriod = 1;
      this.scheduleUnit = 'DAYS';
      this.currentPeriods = this.schedulePeriods.filter(period => period.idUnit === 'DAYS');
      this.scheduleActivated = false;
    }
  }

  formatStartDateStrings(): void {
    const startDate = this.scheduleForm.controls['startDate'].value.format('YYYY-MM-DD');
    const startTime = this.scheduleForm.controls['startTime'].value.format('HH:mm:ss');
    this.scheduleRule.startTime = `${startDate} ${startTime}`;
  }

  formatFinalDateStrings(): void {
    if (this.foreverExecution) {
      this.scheduleRule.stopTime = '';
    } else {
      const stopDate = this.scheduleForm.controls['stopDate'].value.format('YYYY-MM-DD');
      this.scheduleRule.stopTime = `${stopDate} 23:59:59`;
    }
  }

  onUnitSelected(idUnit: string): void {
    this.currentPeriods = ScheduleService.getPeriods().filter(period => period.idUnit === idUnit);
    this.scheduleForm.controls['period'].setValue(this.currentPeriods[0].value);
  }

  onForeverSelected() {
    (this.foreverExecution) ?  this.scheduleForm.controls['stopDate'].disable() :   this.scheduleForm.controls['stopDate'].enable();
    if (this.foreverExecution) this.scheduleForm.controls['stopDate'].setValue('');

  }
  onImmediatelySelected() {
    (this.immediateExecution) ?  this.scheduleForm.controls['startDate'].disable() :   this.scheduleForm.controls['startDate'].enable();
    (this.immediateExecution) ?  this.scheduleForm.controls['startTime'].disable() :   this.scheduleForm.controls['startTime'].enable();
    if (this.immediateExecution) {
      this.scheduleForm.controls['startDate'].setValue('');
      this.scheduleForm.controls['startTime'].setValue('');
    }
  }

  saveSchedule() {
    if (this.adminService.isInLiteMode()) {
      this.datatoolsLiteService.triggerMessage$.next(DataToolsLiteMessage.DATARULES_SCHEDULE);
      return;
    }

    if (this.isRuleFirstInAChain(this.dataRule.ruleData.id)) {
      this.deleteDialogOpen = true;
      const dialogRef = this.dialog.open(DataRulesConfirmDialogComponent, {
        autoFocus: true,
        data: {
          titleIcon: 'DataRules-KyloeIcon',
          title: 'This rule is in a chain, are you sure you want to save this schedule?',
          messageIcon: 'alarm',
          message: 'Rule name: ' + this.dataRule.ruleData.name
        }
      });
      dialogRef.afterClosed().subscribe((data) => {
        if (!data) {
          this.deleteDialogOpen = false;
          return;
        }
        if (!this.immediateExecution)
          this.formatStartDateStrings();

        if (!this.foreverExecution)
          this.formatFinalDateStrings();

        const scheduleData = new ScheduleData(
          this.dataRule.ruleData.id as string,
          this.scheduleRule.startTime,
          this.scheduleRule.stopTime,
          this.scheduleRule.period,
          this.scheduleRule.scheduleUnit,
          this.scheduleRule.enabled
        );
        this.scheduleSaving.emit(scheduleData);
      });
    } else {
      if (!this.immediateExecution)
        this.formatStartDateStrings();

      if (!this.foreverExecution)
        this.formatFinalDateStrings();

      const scheduleData = new ScheduleData(
        this.dataRule.ruleData.id as string,
        this.scheduleRule.startTime,
        this.scheduleRule.stopTime,
        this.scheduleRule.period,
        this.scheduleRule.scheduleUnit,
        this.scheduleRule.enabled
      );
      this.scheduleSaving.emit(scheduleData);
    }
  }

  deleteSchedule(): void {
    const dialogTitle = (this.isRuleFirstInAChain(this.dataRule.ruleData.id)) ?
      'This scheduled rule is in a chain, are you sure you want to remove this schedule?'
      : 'Are you sure you want to remove this schedule?';

    this.deleteDialogOpen = true;
    const dialogRef = this.dialog.open(DataRulesConfirmDialogComponent, {
      autoFocus: true,
      data: {
        titleIcon: 'DataRules-KyloeIcon',
        title: dialogTitle,
        messageIcon: 'alarm',
        message: 'Rule name: ' + this.dataRule.ruleData.name
      }
    });

    dialogRef.afterClosed().subscribe((data) => {
      if (!data) {
        this.deleteDialogOpen = false;
        return;
      }

      this.scheduleDeleting.emit();
      this.deleteDialogOpen = false;
      this.dataRule.viewState.scheduleOpen = false;
    });
  }

  stopDateTitle(): string {
    if (this.scheduleForm.controls['stopDate'].disabled)
      return 'The rule is currently set to run forever';
    if (this.stopDateIsBeforeStartDate)
      return 'Stop date is before the scheduled start date';
    return '';
  }

  private compareStartEndDates(): void {
    const controls = this.scheduleForm.controls;
    this.minStopDate = controls['startDate'].value || this.currentDate;
    if (controls['startDate'].value > controls['stopDate'].value) {
      controls['stopDate'].markAsDirty();
      controls['stopDate'].markAsTouched();
      controls['stopDate'].setErrors({incorrect: true});
      this.stopDateIsBeforeStartDate = true;
    } else {
      this.stopDateIsBeforeStartDate = false;
      if (!controls['foreverIndicator'].value)
        controls['stopDate'].setErrors(null);
    }
    if (!controls['foreverIndicator'].value && !controls['stopDate'].value)
      controls['stopDate'].setErrors({incorrect: true});
    this.isScheduleExpired();
  }

  private refreshScheduleData(): Observable<void> {
    return this.scheduleDataService.initialize(true).pipe(map(_ => {
      this.scheduleData = this.scheduleDataService.getScheduleByRuleId(this.dataRule.ruleData.id as string);
    }));
  }

  ngOnDestroy() {
    document.removeEventListener('click', this.onClick);
  }
}
